import { useAtomValue } from 'jotai';
import lottie, { AnimationItem } from 'lottie-web';
import { useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { dealsNavHref } from 'src/components/deals/dealFilters';
import Logo from 'src/images/logos/logo+icon_svg.svg?react';
import { dealsVendorAtom, vendorHasDealsAtom } from 'src/store';
import { CreateACartBlock, NavUserOrLoginBlock, ReceiveACartBlock, SolutionsDropdown } from '.';

const DEALS_ANIMATION_URL = 'https://assets.share-a-cart.com/images/next/deals-shake.json';

export const NavbarFull = () => {
  const dealsLottieRef = useRef<HTMLDivElement>(null);
  const dealsAnimRef = useRef<AnimationItem | null>(null);
  const dealsVendor = useAtomValue(dealsVendorAtom);
  const vendorHasDeals = useAtomValue(vendorHasDealsAtom);
  const { pathname } = useLocation();
  const pathSlug = pathname.split('/')[1];
  const dealsSource = ['get', 'receive', 'make', 'share'].includes(pathSlug) ? pathSlug : null;

  useEffect(() => {
    if (!dealsLottieRef.current) return;
    dealsAnimRef.current = lottie.loadAnimation({
      container: dealsLottieRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      path: DEALS_ANIMATION_URL,
    });
    return () => dealsAnimRef.current?.destroy();
  }, []);

  useEffect(() => {
    if (vendorHasDeals) {
      dealsAnimRef.current?.goToAndPlay(0);
    } else {
      dealsAnimRef.current?.goToAndStop(0, true);
    }
  }, [vendorHasDeals]);

  return (
    <nav className='inline-flex w-full flex-nowrap items-center justify-between gap-x-4 gap-y-2 overflow-x-clip bg-primary-dark px-2 py-6 font-secondary text-sm font-semibold text-skyBlue xs:px-4 sm:p-6 md:px-8 medLg:px-4 lg:gap-x-6 lg:px-10 xl:px-12'>
      {/* Left side: Logo + Receive/Create joined pill */}
      <div className='flex h-12 w-full flex-row flex-nowrap items-center justify-between gap-6 xs:w-auto xs:justify-start'>
        <NavLink
          to={`/`}
          reloadDocument
          className='flex flex-row px-1 py-1 transition-all active:scale-[97%]'
        >
          <Logo title='Share-A-Cart Logo' />
        </NavLink>

        {/* Receive/Create joined pill (sm+) */}
        <div className='hidden items-center justify-center overflow-hidden rounded-full sm:inline-flex'>
          <ReceiveACartBlock forNavPill='left' />
          <span className='w-px self-stretch bg-white/20' />
          <CreateACartBlock forNavPill='right' />
        </div>
      </div>

      {/* Right side */}
      <div className='flex w-full max-w-fit flex-row flex-nowrap items-center gap-2'>
        {/* Solutions dropdown (desktop only) */}
        <SolutionsDropdown />

        {/* NavUserOrLoginBlock handles: Get Plus+/Login pill (lg+), hamburger (always) */}
        <NavUserOrLoginBlock />

        {/* Deals button (always visible) */}
        <Link
          to={dealsNavHref(dealsVendor, vendorHasDeals, dealsSource)}
          aria-label='Deals'
          className='inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-full bg-customYellow px-2 text-primary-dark transition-all hover:opacity-90 active:scale-[95%] md:h-10'
        >
          <div
            ref={dealsLottieRef}
            style={{ width: '28px', height: '28px', flexShrink: 0, marginBottom: '6px' }}
          />
          {/* <span className='hidden xs:inline font-primary font-semibold text-xl leading-[1.3]'>5</span> */}
        </Link>
      </div>
    </nav>
  );
};
